/**
 * Scroll-depth subscribe popup.
 *
 * Rules:
 * - Trigger at ~60% page scroll depth (not time, not exit-intent)
 * - Skip if user engaged with Join Community this session
 * - Once dismissed: hide for session + 7 days (localStorage)
 * - One-click close (X, dismiss link, overlay, Escape)
 */

import PopupManager from './popup-manager.js';

const SCROLL_DEPTH = 0.6;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const STORAGE_DISMISS = 'pe_mp_subscribe_popup_dismissed_until';
const STORAGE_SESSION = 'pe_mp_subscribe_popup_session_dismissed';
const STORAGE_JOIN = 'pe_mp_join_community_engaged';
const STORAGE_SUBSCRIBED = 'pe_mp_newsletter_subscribed';

const SubscribePopup = {
    shown: false,
    scrollBound: null,

    init() {
        if (!document.getElementById('subscribe-popup-content')) {
            return;
        }

        this.register();
        this.watchJoinCommunity();
        this.watchNewsletterSuccess();

        if (this.shouldSuppress()) {
            return;
        }

        this.scrollBound = this.onScroll.bind(this);
        window.addEventListener('scroll', this.scrollBound, { passive: true });
        // In case the page is already deep (e.g. restored scroll).
        this.onScroll();
    },

    register() {
        PopupManager.register('subscribe', {
            size: 'medium',
            width: '480px',
            className: ['popup__content--subscribe'],
            bodyClasses: ['popup__body--subscribe'],
            preserveFormIds: true,
            skipDefaultFormInit: true,
            onOpen: (container) => {
                this.bindDismiss(container);

                const form = container.querySelector('form[data-form-type="newsletter"]');
                if (form && window.FormSubmission && typeof window.FormSubmission.setupFormHandler === 'function') {
                    window.FormSubmission.setupFormHandler(form);
                }

                if (window.InputLabels && typeof window.InputLabels.init === 'function') {
                    window.InputLabels.init();
                }
            },
            onClose: () => {
                this.markDismissed();
            },
        });
    },

    shouldSuppress() {
        if (this.shown) {
            return true;
        }

        try {
            if (sessionStorage.getItem(STORAGE_SESSION) === '1') {
                return true;
            }
            if (sessionStorage.getItem(STORAGE_JOIN) === '1') {
                return true;
            }
            if (localStorage.getItem(STORAGE_SUBSCRIBED) === '1') {
                return true;
            }
            const until = parseInt(localStorage.getItem(STORAGE_DISMISS) || '0', 10);
            if (until && Date.now() < until) {
                return true;
            }
        } catch (e) {
            // Storage unavailable — still allow popup once per page load.
        }

        return false;
    },

    onScroll() {
        if (this.shouldSuppress()) {
            this.teardownScroll();
            return;
        }

        const doc = document.documentElement;
        const scrollTop = window.scrollY || doc.scrollTop || 0;
        const scrollable = Math.max(doc.scrollHeight - window.innerHeight, 1);
        const depth = scrollTop / scrollable;

        if (depth >= SCROLL_DEPTH) {
            this.open();
        }
    },

    open() {
        if (this.shown || this.shouldSuppress()) {
            return;
        }

        // Don't stack on top of another open popup (e.g. join-community).
        // Keep listening so we can open after the other popup closes.
        if (document.getElementById('popup-manager')) {
            return;
        }

        // Re-check join engagement right before open (race with click handler).
        try {
            if (sessionStorage.getItem(STORAGE_JOIN) === '1') {
                this.teardownScroll();
                return;
            }
        } catch (e) {
            // ignore
        }

        this.shown = true;
        this.teardownScroll();

        const config = PopupManager.getPopupConfig('subscribe');
        PopupManager.openPopupFromHTML('#subscribe-popup-content', {
            ...config,
            popupType: 'subscribe',
            onOpen: config.onOpen,
            onClose: config.onClose,
        });
    },

    bindDismiss(container) {
        const dismissers = container.querySelectorAll('[data-subscribe-popup-dismiss]');
        dismissers.forEach((el) => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                this.dismiss();
            });
        });
    },

    dismiss() {
        this.markDismissed();
        if (window.PopupManager) {
            window.PopupManager.closeAllPopups();
        }
    },

    markDismissed() {
        try {
            sessionStorage.setItem(STORAGE_SESSION, '1');
            localStorage.setItem(STORAGE_DISMISS, String(Date.now() + WEEK_MS));
        } catch (e) {
            // ignore
        }
        this.shown = true;
        this.teardownScroll();
    },

    markSubscribed() {
        try {
            localStorage.setItem(STORAGE_SUBSCRIBED, '1');
            sessionStorage.setItem(STORAGE_SESSION, '1');
        } catch (e) {
            // ignore
        }
        this.shown = true;
        this.teardownScroll();
    },

    watchJoinCommunity() {
        // Any click that opens join-community counts as engaging that funnel.
        document.addEventListener('click', (e) => {
            const trigger = e.target.closest('[data-popup="join-community"]');
            if (!trigger) {
                return;
            }
            try {
                sessionStorage.setItem(STORAGE_JOIN, '1');
            } catch (err) {
                // ignore
            }
            this.teardownScroll();
        }, true);

        // Also catch programmatic opens / hash auto-open after the fact.
        const host = document.getElementById('join-community-popup-content');
        if (host) {
            try {
                if (window.location.hash === '#join') {
                    sessionStorage.setItem(STORAGE_JOIN, '1');
                }
            } catch (e) {
                // ignore
            }
        }
    },

    watchNewsletterSuccess() {
        // When any newsletter form succeeds, never show the popup again.
        document.addEventListener('submit', (e) => {
            const form = e.target;
            if (!(form instanceof HTMLFormElement)) {
                return;
            }
            if (form.getAttribute('data-form-type') !== 'newsletter') {
                return;
            }
            // Mark after a short delay if form still looks successful via MutationObserver on message.
            // Safer: hook FormSubmission success by observing .form-message.success inside the form.
            const observer = new MutationObserver(() => {
                const msg = form.querySelector('.form-message.alert--success, .alert--success');
                if (msg) {
                    this.markSubscribed();
                    observer.disconnect();
                }
            });
            observer.observe(form, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
            setTimeout(() => observer.disconnect(), 15000);
        }, true);
    },

    teardownScroll() {
        if (this.scrollBound) {
            window.removeEventListener('scroll', this.scrollBound);
            this.scrollBound = null;
        }
    },
};

export default SubscribePopup;
